import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdInventory2 } from "react-icons/md";
import "./NewArticle.scss";

interface NewArticleProps {
  onClose: () => void;
}

export default function NewArticle({ onClose }: NewArticleProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";

    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className="new-article-modal"
      aria-labelledby="new-article-title"
      aria-describedby="new-article-description"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form className="new-article-modal__form" onSubmit={(event) => event.preventDefault()}>
        <button
          type="button"
          className="new-article-modal__close"
          onClick={onClose}
          aria-label="Modal schließen"
          autoFocus
        >
          <MdClose aria-hidden="true" />
        </button>

        <header className="new-article-modal__header">
          <h2 id="new-article-title">Neuen Artikel anlegen</h2>
          <p id="new-article-description">
            Erfassen Sie die Artikeldaten. Pflichtfelder sind mit * markiert.
          </p>
        </header>

        <div className="new-article-modal__content">
          <h3 className="new-article-modal__section-title">
            <MdInventory2 aria-hidden="true" /> Artikeldaten
          </h3>

          <fieldset className="new-article-modal__article-types">
            <legend>Artikeltyp *</legend>
            <div className="new-article-modal__type-options">
              <label>
                <input type="radio" name="articleType" value="serial-number" defaultChecked required />
                <span>Mit Seriennummer (Handy)</span>
              </label>
              <label>
                <input type="radio" name="articleType" value="accessories" />
                <span>Zubehör / Sonstiges</span>
              </label>
              <label>
                <input type="radio" name="articleType" value="virtual" />
                <span>Virtueller Artikel</span>
              </label>
            </div>
          </fieldset>

          <div className="new-article-modal__grid">
            <div className="new-article-modal__column">
              <div className="new-article-modal__field">
                <label htmlFor="new-article-name">Artikelname *</label>
                <input id="new-article-name" name="name" type="text" placeholder="Artikelname" required />
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-product-group">Warengruppe</label>
                <select id="new-article-product-group" name="productGroup" defaultValue="">
                  <option value="">Noch keine Warengruppen verfügbar</option>
                </select>
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-number">Artikelnummer *</label>
                <input id="new-article-number" name="articleNumber" type="text" placeholder="Artikelnummer" required />
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-account">Konto</label>
                <select id="new-article-account" name="account" defaultValue="">
                  <option value="">Noch keine Konten verfügbar</option>
                </select>
              </div>
              <div className="new-article-modal__field-row">
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-purchase-net">EK Netto</label>
                  <input id="new-article-purchase-net" name="purchaseNet" type="text" inputMode="decimal" placeholder="0,00 €" />
                </div>
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-sale-gross">VK Brutto</label>
                  <input id="new-article-sale-gross" name="saleGross" type="text" inputMode="decimal" placeholder="0,00 €" />
                </div>
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-stock-text">Lagertext</label>
                <input id="new-article-stock-text" name="stockText" type="text" placeholder="Text zum Lagerartikel" />
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-invoice-text">Zusatztext auf Rechnung</label>
                <textarea id="new-article-invoice-text" name="invoiceText" rows={3} placeholder="Zusätzliche Angaben für die Rechnung" />
              </div>
            </div>

            <div className="new-article-modal__column">
              <div className="new-article-modal__field">
                <label htmlFor="new-article-manufacturer">Hersteller</label>
                <input id="new-article-manufacturer" name="manufacturer" type="text" placeholder="z. B. Apple" />
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-subgroup">Untergruppe</label>
                <select id="new-article-subgroup" name="subgroup" defaultValue="">
                  <option value="">Noch keine Untergruppen verfügbar</option>
                </select>
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-ean">EAN</label>
                <input id="new-article-ean" name="ean" type="text" inputMode="numeric" placeholder="EAN eingeben" />
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-status">Status</label>
                <select id="new-article-status" name="status" defaultValue="">
                  <option value="" disabled>Bitte wählen</option>
                  <option value="active">Aktiv</option>
                  <option value="inactive">Inaktiv</option>
                </select>
              </div>
              <div className="new-article-modal__field-row">
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-sale-net">VK Netto</label>
                  <input id="new-article-sale-net" name="saleNet" type="text" inputMode="decimal" placeholder="0,00 €" />
                </div>
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-revenue">Erlös</label>
                  <input id="new-article-revenue" name="revenue" type="text" inputMode="decimal" defaultValue="0,00" />
                </div>
              </div>
              <div className="new-article-modal__field-row">
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-stock">Bestand (aktuell im Lager)</label>
                  <input id="new-article-stock" name="stock" type="number" min="0" step="1" placeholder="0" />
                </div>
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-target-stock">Soll</label>
                  <input id="new-article-target-stock" name="targetStock" type="number" min="0" step="1" placeholder="0" />
                </div>
              </div>
            </div>

            <div className="new-article-modal__column">
              <div className="new-article-modal__field">
                <label htmlFor="new-article-description-short">Kurzbeschreibung</label>
                <input id="new-article-description-short" name="shortDescription" type="text" placeholder="Kurze Beschreibung des Artikels" />
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-distributor">Distributor</label>
                <select id="new-article-distributor" name="distributor" defaultValue="">
                  <option value="">Noch keine Distributoren verfügbar</option>
                </select>
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-distributor-number">Distributornummer</label>
                <input id="new-article-distributor-number" name="distributorNumber" type="text" placeholder="Distributornummer" />
              </div>
              <div className="new-article-modal__field-row">
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-tax-rate">MwSt Satz</label>
                  <select id="new-article-tax-rate" name="taxRate" defaultValue="19">
                    <option value="19">19 %</option>
                    <option value="7">7 %</option>
                    <option value="0">0 %</option>
                  </select>
                </div>
                <div className="new-article-modal__field">
                  <label htmlFor="new-article-sale-tax">MwSt VK</label>
                  <input id="new-article-sale-tax" name="saleTax" type="text" inputMode="decimal" defaultValue="0,00" />
                </div>
              </div>
              <div className="new-article-modal__field">
                <label htmlFor="new-article-warehouse">Lager</label>
                <select id="new-article-warehouse" name="warehouse" defaultValue="">
                  <option value="">Noch keine Lager verfügbar</option>
                </select>
              </div>
            </div>
          </div>

          <div className="new-article-modal__switches">
            <label className="new-article-modal__switch">
              <input type="checkbox" name="differentialTaxation" role="switch" />
              <span className="new-article-modal__switch-track" aria-hidden="true" />
              <span>Differenzbesteuert</span>
            </label>
            <label className="new-article-modal__switch">
              <input type="checkbox" name="posQuickSelection" role="switch" />
              <span className="new-article-modal__switch-track" aria-hidden="true" />
              <span>POS Schnellwahl</span>
            </label>
            <label className="new-article-modal__switch">
              <input type="checkbox" name="rentalArticle" role="switch" />
              <span className="new-article-modal__switch-track" aria-hidden="true" />
              <span>Mietartikel</span>
            </label>
          </div>

          <div className="new-article-modal__field">
            <label htmlFor="new-article-notes">Bemerkungen</label>
            <textarea id="new-article-notes" name="notes" rows={3} placeholder="Weitere Hinweise zum Artikel" />
          </div>
        </div>

        <footer className="new-article-modal__footer">
          <p id="new-article-save-hint">Die Speicherung wird später angebunden.</p>
          <button type="reset" className="new-article-modal__btn new-article-modal__btn--reset">
            Alles zurücksetzen
          </button>
          <button
            type="submit"
            className="new-article-modal__btn new-article-modal__btn--submit"
            aria-describedby="new-article-save-hint"
            disabled
          >
            Anlegen
          </button>
        </footer>
      </form>
    </dialog>,
    document.body,
  );
}
